import React, { useMemo } from 'react';
import type { Perk } from 'ui/components/PerksList/PerksList';
import { PerksList } from 'ui/components/PerksList/PerksList';
import { useTranslation } from '@whoop/i18n/lang/client';
import clsx from 'clsx';
import LoadingButton from 'ui/components/ButtonV2/LoadingButton';
import { useShallow } from 'zustand/react/shallow';
import PriorityImage from '@/components/PriorityImage/PriorityImage';
import { tierLogoMap, tierHeaderImage } from '@/lib/constants/images';
import { useSiteStore } from '@/store/SiteStoreProvider';
import {
  MembershipTier,
  membershipTierToDeviceDisplayName,
} from '@/lib/constants/maverick';

const membershipTierClasses = {
  [MembershipTier.ONE]: 'bg-one-membership-gradient',
  [MembershipTier.PEAK]: 'bg-peak-membership-gradient',
  [MembershipTier.LIFE]: 'bg-life-membership-gradient',
};
export interface MembershipTierCardProps {
  tierName: MembershipTier;
  perks: Perk[];
  formattedRenewalPricePerYear: string;
  formattedRenewalPricePerPerson?: string;
  usersCurrentTier?: MembershipTier; // Only exists when in upgrade flow
  isAddingToCart: boolean;
  onCardCTAClick: (tierName: MembershipTier) => Promise<void>;
  isSoftSelected: boolean;
  familySize?: number;
  isUpgrade?: boolean;
  isElite?: boolean;
  isDiscounted?: boolean;
  formattedOriginalPrice?: string;
  imageSrc?: string;
}

export default function MembershipTierCard({
  tierName,
  perks,
  formattedRenewalPricePerYear,
  formattedRenewalPricePerPerson,
  usersCurrentTier,
  isAddingToCart,
  onCardCTAClick,
  isSoftSelected,
  familySize,
  isUpgrade = false,
  isElite,
  isDiscounted = false,
  formattedOriginalPrice,
  imageSrc,
}: MembershipTierCardProps): JSX.Element | null {
  const { t } = useTranslation('membershipTiers');
  const { isRafGiftFlow } = useSiteStore(
    useShallow((state) => ({
      isRafGiftFlow: state.isRafGiftFlow,
    })),
  );

  const translatedPerks = perks.map((perk) => ({
    key: t(perk.key),
    iconStyle: perk.iconStyle,
  }));

  const ctaText = useMemo(() => {
    if (usersCurrentTier === tierName) {
      return t('upgradeToDevice', {
        device: membershipTierToDeviceDisplayName[tierName],
      });
      // If the user is a cancelled user in the upgrade flow, usersCurrentTier will be undefined
      // But we still want to show the upgrade cta
    } else if (usersCurrentTier || isUpgrade) {
      return t('upgradeToTierDevice', {
        device: membershipTierToDeviceDisplayName[tierName],
        tier: tierName,
      });
    } else if (isRafGiftFlow) {
      return t('giftTier', {
        tier: tierName,
      });
    } else {
      return t('startWithTier', {
        tier: tierName,
      });
    }
  }, [usersCurrentTier, tierName, isRafGiftFlow]);

  const { renewalBannerPrice, renewalBannerText } = useMemo(() => {
    const renewalBannerPrice = familySize
      ? formattedRenewalPricePerPerson
      : formattedRenewalPricePerYear;
    let renewalBannerText = 'availableAt';
    if (isDiscounted && !isUpgrade) {
      renewalBannerText = 'availableAtNoYear';
    }
    if (usersCurrentTier || isUpgrade) {
      renewalBannerText = familySize ? 'renewsAtFamily' : 'renewsAt';
    }
    return { renewalBannerPrice, renewalBannerText };
  }, [
    usersCurrentTier,
    familySize,
    formattedRenewalPricePerPerson,
    formattedRenewalPricePerYear,
  ]);

  return (
    <div
      className={clsx(
        'relative flex h-full w-[304px] flex-col rounded-3xl border-2 border-opacity-100 bg-white pb-10 pt-11 shadow-lg transition-all duration-300',
        membershipTierClasses[tierName],
        isSoftSelected ? 'scale-105 border-2 border-black' : 'border-gray-40',
      )}
      data-testid='membership-tier-card'
    >
      {usersCurrentTier === tierName ? (
        <div className='absolute top-0 w-full rounded-t-3xl bg-white bg-opacity-40 px-4 py-[6px] text-center text-xs font-bold'>
          {t('currentMembership')}
        </div>
      ) : null}
      <div className='flex w-full justify-center px-5 text-center'>
        <PriorityImage
          alt={`membership-tier-card-image-${tierName}`}
          className='h-5 select-none'
          src={tierLogoMap[tierName]}
        />
      </div>

      <div className='flex justify-center pt-6'>
        <PriorityImage
          src={imageSrc || tierHeaderImage[tierName]}
          alt={`WHOOP App & Band - ${tierName}`}
          className='h-[201px] w-[304px] select-none'
        />
      </div>

      {!isElite ? (
        <div className='flex select-none justify-center gap-1 bg-white bg-opacity-40 px-3 py-2'>
          <p className='text-sm font-bold'>
            {t(renewalBannerText, { price: renewalBannerPrice })}
            {isDiscounted && formattedOriginalPrice ? (
              <span className="before:border-gray-b50 text-gray-b50 relative ml-1 before:absolute before:-left-0.5 before:-right-0.5 before:top-[45%] before:-rotate-[12deg] before:border-t-[1.5px] before:content-['']">
                {formattedOriginalPrice}
              </span>
            ) : null}
          </p>
        </div>
      ) : null}

      <PerksList
        className='flex-grow bg-opacity-30 px-4 py-6'
        perks={translatedPerks}
      />

      <div className='px-4'>
        <LoadingButton
          className='rounded-2xl text-sm disabled:border-gray-500'
          disabled={isAddingToCart}
          onClick={async () => {
            await onCardCTAClick(tierName);
          }}
          size='medium'
          type='button'
          variant='secondary'
        >
          {ctaText}
        </LoadingButton>
      </div>
      {familySize ? (
        <div className='w-full pt-3 text-center text-sm'>
          {t('familyPerYearPricing', {
            price: formattedRenewalPricePerYear,
            size: familySize,
          })}
        </div>
      ) : null}
    </div>
  );
}
